import * as React from "react";
import { observer } from "mobx-react-lite";
import { Utils } from "../../common/misc/Utils";
import { CloseOutlined, LinkOutlined } from "@ant-design/icons";
import { Tooltip, Button, Tag, Divider } from "antd";
import AttachmentIcon from "./AttachmentIcon";
import { ActionEvent, StepHistoryStatus } from "../types";
import { TaskViewVisualStore } from "../stores";

type Props = {
    fileName: string;
    fileSizeBytes: number;
    className?: string;
    attachmentId?: string | undefined;
    store: TaskViewVisualStore;
    onClick?: () => void;
    onRemove?: () => void;
    onPreview?: (attachmentId: string | undefined) => void;
    uploadDate?: string;
    actionEvent?: ActionEvent;
};

const uploadStatuses = [
    StepHistoryStatus.Starting,
    StepHistoryStatus.Uploading,
    StepHistoryStatus.Preparing,
    StepHistoryStatus.MonitoringUploadStatus,
];

const TaskAttachment: React.FC<Props> = ({
    store,
    fileName,
    fileSizeBytes,
    onRemove,
    onClick,
    onPreview,
    actionEvent,
    className = "",
    attachmentId = undefined,
    uploadDate = "",
}) => {
    const removeFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onRemove) {
            onRemove();
        }
    };

    const handleDoubleClick = () => {
        if (!onPreview) {
            return;
        }
        const extension = fileName.split(".").pop();

        switch (extension) {
            case "pdf":
            case "txt":
                onPreview(attachmentId);
                break;
            default:
                break;
        }
    };

    const getErrorMessage = () => {
        return actionEvent!.stepHistory[actionEvent!.stepHistory.length - 1]
            ?.errorMessage;
    };

    const getStepStatusDisplay = () => {
        if (
            !actionEvent?.stepHistory ||
            actionEvent?.stepHistory.length === 0 ||
            actionEvent?.actionType !== "UploadAndAnalyzeInAI"
        ) {
            return null;
        }

        const lastStep =
            actionEvent?.stepHistory[actionEvent.stepHistory.length - 1].status;

        const uploadCompleted =
            !!actionEvent?.stepHistory.find(
                (s) => s.status === StepHistoryStatus.UploadCompleted,
            )?.endTimestamp ||
            actionEvent?.stepHistory[1]?.status === StepHistoryStatus.Analyzing;

        const analyzingCompleted =
            !!actionEvent?.stepHistory.find(
                (s) => s.status === StepHistoryStatus.Analyzing,
            )?.endTimestamp && lastStep !== StepHistoryStatus.Failed;

        const analysisInProgress =
            lastStep === StepHistoryStatus.Analyzing ||
            (!!actionEvent?.stepHistory.find(
                (i) => i.status === StepHistoryStatus.Analyzing,
            )?.endTimestamp &&
                actionEvent?.status === "Started");

        return (
            <div className="attachment-steps">
                <div
                    style={{ paddingBottom: 4 }}
                    className={`status ${uploadCompleted ? "completed" : ""} ${uploadStatuses.includes(lastStep) ? "in-progress" : ""}`}
                >{`Uploading${uploadStatuses.includes(lastStep) ? "..." : ""}`}</div>
                <div
                    className={`status ${analyzingCompleted ? "completed" : ""} ${analysisInProgress ? "in-progress" : ""}`}
                >{`Analyzing${analysisInProgress ? "..." : ""}`}</div>
            </div>
        );
    };

    const handleRetryClick = () => {
        store.executeAction(
            actionEvent!.taskId,
            actionEvent!.actionDefinitionId,
            {},
            false,
            attachmentId,
        );
    };

    const getFinalStatusContainer = () => {
        //TODO: Create enum for action types
        if (actionEvent?.actionType !== "UploadAndAnalyzeInAI") {
            return null;
        }
        let className = "";
        let statusLabel = "";
        const analyzingCompleted =
            actionEvent?.stepHistory[actionEvent?.stepHistory.length - 1]
                ?.status === StepHistoryStatus.Attention;
        if (analyzingCompleted) {
            className = "analysed";
            statusLabel = "Analysis finished";
        } else if (actionEvent?.status === "Started") {
            className = "in-progress";
            statusLabel = "In progress...";
        } else if (actionEvent?.status === "Failed") {
            className = "failed";
            statusLabel = "Error";
        } else if (actionEvent?.status === "Completed") {
            className = "submitted";
            statusLabel = "Submitted";
        }
        if (!className) {
            return null;
        }

        const getTooltip = () => {
            if (className === "failed") {
                return actionEvent?.message;
            }
            if (className === "submitted") {
                return `Contract successfully created${actionEvent?.result && Object.keys(actionEvent.result).length > 0 ? `:${JSON.stringify(actionEvent.result)}` : "."}`;
            }
            return undefined;
        };

        return (
            <div className="status-container">
                <Tooltip title={getTooltip()}>
                    <Tag className={className}>{statusLabel}</Tag>
                </Tooltip>
            </div>
        );
    };

    const isLast = () => {
        return (
            store.taskAttachments[store.taskAttachments.length - 1].id ===
            attachmentId
        );
    };

    return (
        <>
            <div
                className={`attachment ${className} ${!actionEvent?.status ? "not-analyzed" : ""}`}
                onClick={onClick}
                onDoubleClick={handleDoubleClick}
            >
                {onRemove && (
                    <span className="attachment-remove-button">
                        <Button
                            shape="circle"
                            type="link"
                            size="small"
                            icon={<CloseOutlined />}
                            style={{ color: "#273C75" }}
                            onClick={removeFile}
                        />
                    </span>
                )}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <AttachmentIcon fileName={fileName} />
                    <Tooltip
                        title={fileName}
                        overlayStyle={{ wordBreak: "break-all" }}
                    >
                        <div className="attachment-name">{fileName}</div>
                    </Tooltip>
                </div>
                <div style={{ paddingLeft: 32 }}>
                    <div className="attachment-size">
                        {uploadDate
                            ? `${Utils.formatDateStringShort(uploadDate)}, `
                            : null}
                        {Utils.readableFileSize(fileSizeBytes, true)}
                    </div>
                    {getStepStatusDisplay()}
                    <div className="attachment-actions">
                        {actionEvent?.resultUrl &&
                        actionEvent?.status !== "Failed" ? (
                            <>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        window.open(
                                            actionEvent.resultUrl,
                                            "_blank",
                                        );
                                    }}
                                    style={{ marginRight: 10 }}
                                >
                                    Open session
                                </Button>
                                <Button
                                    icon={<LinkOutlined />}
                                    onClick={() =>
                                        Utils.copyTextToClipboard(
                                            actionEvent!.resultUrl!,
                                        )
                                    }
                                >
                                    Copy link
                                </Button>
                            </>
                        ) : actionEvent?.status === "Failed" &&
                          !getErrorMessage()?.endsWith("Broken") ? (
                            <Button
                                type="primary"
                                danger
                                onClick={handleRetryClick}
                            >
                                Retry
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
            {getFinalStatusContainer()}
            {(store?.taskAttachments?.length ?? 0) > 1 && !isLast() && (
                <Divider style={{ margin: 0 }} />
            )}
        </>
    );
};

export default observer(TaskAttachment);
